/**
 * LayoutSanitizer - Security & Validation Layer for Custom Track Layouts
 * Enforces schema validation, payload size limits, boundary constraints,
 * ID generation, jump loop prevention, and protection against prototype pollution / XSS.
 * Strips icon and color upon export and enforces system icon and color upon import.
 */

export const MAX_LAYOUT_JSON_LENGTH = 65536; // 64 KB
export const MAX_CUSTOM_LAYOUTS = 50;
export const MAX_ITEMS_PER_LAYOUT = 100;
export const MAX_NAME_LENGTH = 50;
export const ALLOWED_BOARD_TARGETS = Object.freeze([121, 241, 361, 481]);

export const BRAND_GAME_TITLES = Object.freeze({
  clash: 'Cribbage Clash',
  pints: 'Pints & Pegs',
  adult: 'Skunked & Stripped'
});

export const BRAND_ALLOWED_TYPES = Object.freeze({
  clash: [
    'jail', 'bar', 'timeout', 'jump', 'noPassingLanes',
    'stopSign', 'stopSigns', 'signs', 'signBlock', 'detour',
    'roundAbout', 'roundabout', 'dice', 'diceRoll',
    'wheel', 'spinningWheel', 'spinWheel'
  ],
  pints: [
    'youDrink', 'guysDrink', 'galsDrink', 'leftDrink',
    'rightDrink', 'everyoneDrink', 'everyoneButYouDrink', 'pickWhoDrinks'
  ],
  adult: [
    'loseClothing', 'gainClothing', 'pickWhoLosesClothing',
    'rightLosesClothing', 'leftLosesClothing', 'removeClothingChoice',
    'touchBreast', 'lickBreast', 'suckBreast',
    'touchGenitals', 'lickGenitals', 'suckGenitals', 'fingerGenitals'
  ]
});

// Canonical mapping for allowed aliases
export const TYPE_ALIASES = Object.freeze({
  stopSigns: 'stopSign',
  roundabout: 'roundAbout',
  signBlock: 'signs',
  diceRoll: 'dice',
  spinningWheel: 'wheel',
  spinWheel: 'wheel'
});

export const SYSTEM_TYPE_METADATA = Object.freeze({
  // Clash
  jail: { icon: '🔒', color: '#94a3b8', name: 'Jail' },
  bar: { icon: '🍸', color: '#f59e0b', name: 'The Bar' },
  timeout: { icon: '⏳', color: '#8b5cf6', name: 'Timeout' },
  jump: { icon: '⚡', color: '#3b82f6', name: 'Jump' },
  noPassingLanes: { icon: '🚫', color: '#ef4444', name: 'No Passing' },
  stopSign: { icon: '🛑', color: '#ef4444', name: 'Stop Sign' },
  signs: { icon: '🪧', color: '#ec4899', name: 'Sign Block' },
  detour: { icon: '🔀', color: '#06b6d4', name: 'Detour' },
  roundAbout: { icon: '🔄', color: '#10b981', name: 'Roundabout' },
  dice: { icon: '🎲', color: '#a855f7', name: 'Dice' },
  diceRoll: { icon: '🎲', color: '#a855f7', name: 'Dice' },
  wheel: { icon: '🎡', color: '#ec4899', name: 'Spinning Wheel' },
  spinningWheel: { icon: '🎡', color: '#ec4899', name: 'Spinning Wheel' },
  spinWheel: { icon: '🎡', color: '#ec4899', name: 'Spinning Wheel' },

  // Pints
  youDrink: { icon: '🍺', color: '#f59e0b', name: 'You Drink' },
  guysDrink: { icon: '🍻', color: '#6366f1', name: 'Guys Drink' },
  galsDrink: { icon: '🍷', color: '#f43f5e', name: 'Gals Drink' },
  leftDrink: { icon: '👈', color: '#3b82f6', name: 'Left Drinks' },
  rightDrink: { icon: '👉', color: '#06b6d4', name: 'Right Drinks' },
  everyoneDrink: { icon: '🥂', color: '#10b981', name: 'Everyone Drinks' },
  everyoneButYouDrink: { icon: '🎉', color: '#8b5cf6', name: 'Everyone But You' },
  pickWhoDrinks: { icon: '🎯', color: '#ec4899', name: 'Pick Who Drinks' },

  // Adult
  loseClothing: { icon: '👙', color: '#f43f5e', name: 'You Strip' },
  gainClothing: { icon: '🧥', color: '#10b981', name: 'Gain Clothing' },
  pickWhoLosesClothing: { icon: '🎯', color: '#ec4899', name: 'Pick Who Strips' },
  rightLosesClothing: { icon: '👉', color: '#3b82f6', name: 'Right Strips' },
  leftLosesClothing: { icon: '👈', color: '#8b5cf6', name: 'Left Strips' },
  removeClothingChoice: { icon: '✂️', color: '#e11d48', name: 'Remove Choice' },
  touchBreast: { icon: '💋', color: '#f43f5e', name: 'Touch Breast' },
  lickBreast: { icon: '👅', color: '#ec4899', name: 'Lick Breast' },
  suckBreast: { icon: '👄', color: '#d946ef', name: 'Suck Breast' },
  touchGenitals: { icon: '✨', color: '#a855f7', name: 'Touch Genitals' },
  lickGenitals: { icon: '👅', color: '#f43f5e', name: 'Lick Genitals' },
  suckGenitals: { icon: '👄', color: '#ec4899', name: 'Suck Genitals' },
  fingerGenitals: { icon: '👉', color: '#f59e0b', name: 'Finger Genitals' }
});

export class LayoutSanitizer {
  /**
   * Retrieves system-defined official icon, color, and name for an obstacle type.
   * @param {string} type
   * @param {string} [brand='clash']
   * @returns {{ icon: string, color: string, name: string }}
   */
  static getSystemMetaForType(type, brand = 'clash') {
    const canonicalType = TYPE_ALIASES[type] || type;
    const meta = SYSTEM_TYPE_METADATA[canonicalType];
    if (meta) {
      return { ...meta };
    }
    return {
      icon: '★',
      color: '#f59e0b',
      name: canonicalType || 'Obstacle'
    };
  }

  /**
   * Generates a cryptographically or pseudo-random safe ID.
   * Format: [prefix]_[timestamp]_[alphanumeric]
   * @param {string} [prefix='layout']
   * @returns {string}
   */
  static generateSafeId(prefix = 'layout') {
    const cleanPrefix = String(prefix || 'layout').replace(/[^a-zA-Z0-9_]/g, '').slice(0, 20);
    const rnd = Math.random().toString(36).substring(2, 9);
    return `${cleanPrefix}_${Date.now()}_${rnd}`;
  }

  /**
   * Ensures an ID consists strictly of safe alphanumeric characters, dashes, and underscores.
   * If invalid, a fresh safe ID is returned.
   * @param {*} id
   * @param {string} [prefix='layout']
   * @returns {string}
   */
  static sanitizeId(id, prefix = 'layout') {
    if (typeof id === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(id)) {
      return id;
    }
    return LayoutSanitizer.generateSafeId(prefix);
  }

  /**
   * Validates whether a value represents a valid integer.
   * Accepts integer numbers (e.g. 5) or integer strings (e.g. "5").
   * Rejects floats (5.5, "5.5"), booleans, null, undefined, NaN, Infinity, and non-numeric strings.
   * @param {*} val
   * @returns {boolean}
   */
  static isInteger(val) {
    if (val === null || val === undefined || val === '') return false;
    if (typeof val === 'boolean') return false;
    if (typeof val === 'number') {
      return Number.isInteger(val);
    }
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (!trimmed) return false;
      if (!/^-?\d+$/.test(trimmed)) return false;
      const num = Number(trimmed);
      return Number.isSafeInteger(num);
    }
    return false;
  }

  /**
   * Validates that a field is plain text:
   * - Must be a string
   * - Must not contain extended characters (code point > 127) or control characters (< 32 or === 127)
   * - Must not contain XML or SVG tags, markup, namespaces, or keywords (<, >, xml, svg, xmlns)
   * @param {*} val
   * @param {string} fieldName
   * @param {string} [context='']
   * @throws {Error} if validation fails
   */
  static validatePlainTextField(val, fieldName, context = '') {
    if (val === null || val === undefined) return;
    const ctx = context ? ` ${context}` : '';
    if (typeof val !== 'string') {
      throw new Error(`Invalid ${fieldName}${ctx}: must be a plain text string.`);
    }

    // Check for extended characters (code > 127) or control characters (< 32 or === 127)
    for (let i = 0; i < val.length; i++) {
      const code = val.charCodeAt(i);
      if (code > 127) {
        throw new Error(`Invalid ${fieldName}${ctx}: contains extended character "${val[i]}" (code ${code}). Only plain ASCII text is allowed.`);
      }
      if (code < 32 || code === 127) {
        throw new Error(`Invalid ${fieldName}${ctx}: contains control character (code ${code}). Only printable plain text is allowed.`);
      }
    }

    // Check for XML and SVG
    if (/[<>]|&lt;|&gt;|xmlns|svg|xml/i.test(val)) {
      throw new Error(`Invalid ${fieldName}${ctx}: XML and SVG are not allowed.`);
    }
  }

  /**
   * Validates and parses raw JSON string with size checks and prototype pollution guards.
   * @param {string} jsonStr
   * @returns {Object|Array}
   */
  static parseJsonSafely(jsonStr) {
    if (typeof jsonStr !== 'string') {
      throw new Error('Expected JSON layout string.');
    }
    const trimmed = jsonStr.trim();
    if (!trimmed) {
      throw new Error('JSON layout is empty.');
    }
    if (trimmed.length > MAX_LAYOUT_JSON_LENGTH) {
      throw new Error(`Layout payload exceeds maximum size limit of ${MAX_LAYOUT_JSON_LENGTH / 1024}KB.`);
    }

    let parsed;
    try {
      parsed = JSON.parse(trimmed);
    } catch (e) {
      throw new Error(`JSON syntax error: ${e.message}`);
    }

    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Invalid layout format: must be a JSON object or array of objects.');
    }

    // Guard against prototype pollution keys at root
    if (Object.prototype.hasOwnProperty.call(parsed, '__proto__') ||
        Object.prototype.hasOwnProperty.call(parsed, 'constructor') ||
        Object.prototype.hasOwnProperty.call(parsed, 'prototype')) {
      throw new Error('Layout contains disallowed prototype property.');
    }

    return parsed;
  }

  /**
   * Validates and sanitizes a layout's obstacle or action items.
   * Enforces brand whitelists, integer bounds [1, target], self-jump prevention,
   * and jump loop detection.
   * ALWAYS sets icon and color to the system-defined official values for the type.
   * @param {Array} rawItems
   * @param {number} target
   * @param {string} [brand='clash']
   * @param {Object} [options={}]
   * @returns {Array}
   */
  static sanitizeItems(rawItems, target = 481, brand = 'clash', options = {}) {
    const opts = { validateTypes: true, ...options };
    if (!Array.isArray(rawItems)) {
      if (opts.validateTypes && rawItems != null) {
        throw new Error('Layout items property must be an array.');
      }
      return [];
    }

    const allowedTypes = new Set(BRAND_ALLOWED_TYPES[brand] || BRAND_ALLOWED_TYPES.clash);
    const sanitized = [];
    const jumpMap = new Map(); // from -> to (cycle detection)

    const maxItems = Math.min(rawItems.length, MAX_ITEMS_PER_LAYOUT);

    for (let i = 0; i < maxItems; i++) {
      const it = rawItems[i];
      if (!it || typeof it !== 'object' || Array.isArray(it)) {
        if (opts.validateTypes) {
          throw new Error(`Invalid item at position ${i + 1}: expected an obstacle object.`);
        }
        continue;
      }

      // Disallow dangerous keys
      if (Object.prototype.hasOwnProperty.call(it, '__proto__') ||
          Object.prototype.hasOwnProperty.call(it, 'constructor') ||
          Object.prototype.hasOwnProperty.call(it, 'prototype')) {
        continue;
      }

      if (opts.validateTypes) {
        if (it.id !== undefined && it.id !== null) {
          LayoutSanitizer.validatePlainTextField(it.id, 'id', `at item #${i + 1}`);
          if (typeof it.id !== 'string' || !/^[a-zA-Z0-9_-]{1,64}$/.test(it.id)) {
            throw new Error(`Invalid id at item #${i + 1}: must be 1-64 alphanumeric characters, underscores, or hyphens.`);
          }
        }
      }

      if (it.type == null) {
        if (opts.validateTypes) {
          const gameTitle = BRAND_GAME_TITLES[brand] || brand;
          throw new Error(`Missing required "type" field at item #${i + 1} for ${gameTitle}.`);
        }
        continue;
      }

      if (opts.validateTypes) {
        LayoutSanitizer.validatePlainTextField(it.type, 'type', `at item #${i + 1}`);
      }

      const rawType = String(it.type).trim();
      if (!rawType) {
        if (opts.validateTypes) {
          const gameTitle = BRAND_GAME_TITLES[brand] || brand;
          throw new Error(`Missing required "type" field at item #${i + 1} for ${gameTitle}.`);
        }
        continue;
      }

      if (!allowedTypes.has(rawType)) {
        if (opts.validateTypes) {
          const gameTitle = BRAND_GAME_TITLES[brand] || brand;
          const displayTypes = Array.from(allowedTypes)
            .filter(t => !['stopSigns', 'roundabout', 'signBlock'].includes(t))
            .join(', ');
          throw new Error(`Invalid obstacle type "${rawType}" at item #${i + 1} for ${gameTitle}. Valid types are: ${displayTypes}`);
        }
        continue;
      }

      const type = TYPE_ALIASES[rawType] || rawType;
      if (!LayoutSanitizer.isInteger(it.hole)) {
        if (opts.validateTypes) {
          throw new Error(`Invalid hole coordinate "${it.hole}" at item #${i + 1} (${type}): hole must be an integer.`);
        }
        continue;
      }

      const hole = Number(it.hole);
      if (hole < 1 || hole >= target) {
        if (opts.validateTypes) {
          throw new Error(`Invalid hole coordinate "${it.hole}" at item #${i + 1} (${type}): hole must be an integer less than target (${target}) and at least 1 (1 to ${target - 1}).`);
        }
        continue;
      }

      if (opts.validateTypes) {
        if (it.name !== undefined && it.name !== null) {
          LayoutSanitizer.validatePlainTextField(it.name, 'name', `at item #${i + 1} (${type})`);
        }
      }

      // Use system official icon, color, and default name
      const meta = LayoutSanitizer.getSystemMetaForType(type, brand);
      const cleanItem = {
        type,
        hole,
        name: (it.name && typeof it.name === 'string') ? it.name.trim().substring(0, 50) : meta.name,
        icon: meta.icon,
        color: meta.color
      };

      if (it.id && typeof it.id === 'string' && /^[a-zA-Z0-9_-]{1,64}$/.test(it.id)) {
        cleanItem.id = it.id;
      }

      // Length property (e.g. noPassingLanes, signs)
      if (it.length !== undefined && it.length !== null) {
        if (!LayoutSanitizer.isInteger(it.length)) {
          if (opts.validateTypes) {
            throw new Error(`Invalid length "${it.length}" at item #${i + 1} (${type}): length must be an integer.`);
          }
          continue;
        }
        const len = Number(it.length);
        if (len < 1 || len > 5) {
          if (opts.validateTypes) {
            throw new Error(`Invalid length "${it.length}" at item #${i + 1} (${type}): length must be an integer of 5 or less (1 to 5).`);
          }
          continue;
        }
        cleanItem.length = len;
      } else if (type === 'noPassingLanes') {
        cleanItem.length = 5;
      }

      // Destination property for jumps or detours
      if (it.dest != null || type === 'jump') {
        if (it.dest != null && !LayoutSanitizer.isInteger(it.dest)) {
          if (opts.validateTypes) {
            throw new Error(`Invalid jump destination "${it.dest}" at Hole ${hole} (item #${i + 1}): destination must be an integer.`);
          }
          continue;
        }
        const dest = Number(it.dest);
        if (Number.isInteger(dest) && dest >= 1 && dest <= target && dest !== hole) {
          // Check for direct jump cycle (e.g. A -> B and B -> A)
          if (jumpMap.get(dest) === hole) {
            // Drop second jump to prevent infinite ping-pong loop
            continue;
          }
          cleanItem.dest = dest;
          jumpMap.set(hole, dest);
        } else if (type === 'jump') {
          if (opts.validateTypes) {
            throw new Error(`Invalid jump destination "${it.dest}" at Hole ${hole} (item #${i + 1}). Destination must be between 1 and ${target} and different from start hole.`);
          }
          continue;
        }
      }

      sanitized.push(cleanItem);
    }

    return sanitized;
  }

  /**
   * Sanitizes a complete layout object.
   * @param {Object} rawLayout
   * @param {string} [brand='clash']
   * @param {Object} [options={}]
   * @returns {Object}
   */
  static sanitizeLayout(rawLayout, brand = 'clash', options = {}) {
    if (!rawLayout || typeof rawLayout !== 'object') {
      throw new Error('Invalid layout object.');
    }

    const opts = {
      regenerateId: true,
      validateTypes: true,
      ...options
    };

    const defaultTarget = brand === 'clash' ? 481 : 121;
    let target = parseInt(rawLayout.target, 10);
    if (!ALLOWED_BOARD_TARGETS.includes(target)) {
      target = defaultTarget;
    }

    // Items sanitization with type validation and system icon/color assignment
    const items = LayoutSanitizer.sanitizeItems(rawLayout.items, target, brand, opts);

    // Plain text, extended characters, XML, and SVG validation for layout id and name
    if (opts.validateTypes) {
      if (rawLayout.id !== undefined && rawLayout.id !== null) {
        LayoutSanitizer.validatePlainTextField(rawLayout.id, 'id', 'for layout');
      }
      if (rawLayout.name !== undefined && rawLayout.name !== null) {
        LayoutSanitizer.validatePlainTextField(rawLayout.name, 'name', 'for layout');
      }
    }

    // Name sanitization: strip control characters, limit length
    let rawName = typeof rawLayout.name === 'string' ? rawLayout.name : 'Custom Layout';
    let name = rawName.replace(/[\x00-\x1F\x7F]/g, '').trim().substring(0, MAX_NAME_LENGTH);
    if (!name) name = 'Custom Layout';

    // ID resolution
    const id = opts.regenerateId
      ? LayoutSanitizer.generateSafeId(`${brand}_layout`)
      : LayoutSanitizer.sanitizeId(rawLayout.id, `${brand}_layout`);

    const now = Date.now();
    const createdAt = (typeof rawLayout.createdAt === 'number' || typeof rawLayout.createdAt === 'string')
      ? rawLayout.createdAt
      : now;

    const sanitized = {
      id,
      name,
      target,
      items,
      createdAt,
      updatedAt: now
    };

    // config is excluded from imported layouts to keep items as the canonical source of truth

    return sanitized;
  }

  /**
   * Prepares a layout (or list of layouts) for export.
   * Strips icon and color from all items, and strips root metadata (id, createdAt, updatedAt, metrics, config).
   * @param {Object|Array} layoutOrLayouts
   * @returns {Object|Array}
   */
  static prepareForExport(layoutOrLayouts) {
    if (!layoutOrLayouts) return null;
    if (Array.isArray(layoutOrLayouts)) {
      return layoutOrLayouts.map(l => LayoutSanitizer.prepareForExport(l));
    }
    if (typeof layoutOrLayouts !== 'object') return layoutOrLayouts;

    const copy = { ...layoutOrLayouts };
    delete copy.id;
    delete copy.createdAt;
    delete copy.updatedAt;
    delete copy.metrics;
    delete copy.config;

    if (Array.isArray(copy.items)) {
      copy.items = copy.items.map(it => {
        const itemCopy = { ...it };
        delete itemCopy.icon;
        delete itemCopy.color;
        return itemCopy;
      });
    }
    return copy;
  }

  /**
   * Enforces custom layout storage limit to prevent localStorage quota exhaustion.
   * If existing layout array exceeds MAX_CUSTOM_LAYOUTS, oldest layouts are removed.
   * @param {Array} layouts
   * @param {number} [maxCount=MAX_CUSTOM_LAYOUTS]
   * @returns {Array}
   */
  static enforceStorageQuota(layouts, maxCount = MAX_CUSTOM_LAYOUTS) {
    if (!Array.isArray(layouts)) return [];
    if (layouts.length <= maxCount) return layouts;
    return layouts.slice(0, maxCount);
  }
}

if (typeof window !== 'undefined') {
  window.LayoutSanitizer = LayoutSanitizer;
  window.MAX_LAYOUT_JSON_LENGTH = MAX_LAYOUT_JSON_LENGTH;
  window.MAX_CUSTOM_LAYOUTS = MAX_CUSTOM_LAYOUTS;
  window.BRAND_GAME_TITLES = BRAND_GAME_TITLES;
  window.BRAND_ALLOWED_TYPES = BRAND_ALLOWED_TYPES;
  window.SYSTEM_TYPE_METADATA = SYSTEM_TYPE_METADATA;
}
